import TramitesAPI from 'dashboard/api/tramites';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  meta: {
    count: 0,
    currentPage: 1,
    totalPages: 1,
  },
  stats: {},
};

const getters = {
  getTramites: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getStats: $state => $state.stats,
  getTramiteById: $state => id =>
    $state.records.find(tramite => Number(tramite.id) === Number(id)),
};

const actions = {
  async fetchTramites({ commit }, params = {}) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await TramitesAPI.get(params);
      commit('setTramites', response.data.payload);
      commit('setMeta', response.data.meta);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTramite({ commit }, { id }) {
    commit('setUIFlag', { isFetchingItem: true });
    try {
      const response = await TramitesAPI.show(id);
      commit('addOrUpdateTramite', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetchingItem: false });
    }
  },

  async fetchMeta({ commit }) {
    const response = await TramitesAPI.meta();
    commit('setStats', response.data.meta || response.data);
    return response.data;
  },

  async createTramite({ commit }, tramiteData) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await TramitesAPI.create(tramiteData);
      commit('addOrUpdateTramite', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateTramite({ commit }, { id, ...tramiteData }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await TramitesAPI.update(id, tramiteData);
      commit('addOrUpdateTramite', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteTramite({ commit }, id) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await TramitesAPI.delete(id);
      commit('removeTramite', id);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async assignTramite({ commit }, { id, assigneeId }) {
    const response = await TramitesAPI.assign(id, assigneeId);
    commit('addOrUpdateTramite', response.data);
    return response.data;
  },

  async advanceStage({ commit }, { id, actionName, comment }) {
    const response = await TramitesAPI.advanceStage(id, { actionName, comment });
    commit('addOrUpdateTramite', response.data);
    return response.data;
  },

  async submitTramite({ commit }, id) {
    const response = await TramitesAPI.submit(id);
    commit('addOrUpdateTramite', response.data);
    return response.data;
  },

  async recordPayment({ commit }, { id, status, reference, amount }) {
    const response = await TramitesAPI.recordPayment(id, {
      status,
      reference,
      amount,
    });
    commit('addOrUpdateTramite', response.data);
    return response.data;
  },

  async fetchTimeline(_, { id }) {
    const response = await TramitesAPI.timeline(id);
    return response.data;
  },

  async fetchComments(_, { id }) {
    const response = await TramitesAPI.getComments(id);
    return response.data;
  },

  async createComment(_, { id, ...payload }) {
    const response = await TramitesAPI.createComment(id, payload);
    return response.data;
  },
};

const mutations = {
  setTramites($state, tramites) {
    $state.records = tramites || [];
  },

  setMeta($state, meta = {}) {
    $state.meta = {
      count: meta.count || 0,
      currentPage: meta.current_page || 1,
      totalPages: meta.total_pages || 1,
    };
  },

  setStats($state, stats) {
    $state.stats = stats || {};
  },

  addOrUpdateTramite($state, tramite) {
    if (!tramite || !tramite.id) return;
    const index = $state.records.findIndex(t => t.id === tramite.id);
    if (index !== -1) {
      $state.records.splice(index, 1, tramite);
    } else {
      $state.records.push(tramite);
    }
  },

  removeTramite($state, id) {
    $state.records = $state.records.filter(tramite => tramite.id !== id);
  },

  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
